import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Its own small namespace, never `../../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import { channelsSkeletonKind, type ChannelsSkeletonKind } from './channelsSkeletonKind';
import './ChannelsSkeleton.css';

/** Cards per category, as the catalog lays them out: Web 2, Phone 1, Messaging 2. */
const CATEGORY_CARDS = [2, 1, 2];
/** Sections drawn under a detail's activation row — enough to fill a laptop screen. */
const DETAIL_SECTIONS = [0, 1, 2];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`channels-skeleton__bone ${className}`} />
);

/** One channel card (`ChannelCard`): icon, name and tag, description; status and action. */
const CardSkeleton: React.FC = () => (
  <div className="channels-skeleton__card">
    <Bone className="channels-skeleton__icon" />
    <div className="channels-skeleton__card-text">
      <Bone className="channels-skeleton__line channels-skeleton__line--name" />
      <Bone className="channels-skeleton__line channels-skeleton__line--desc" />
    </div>
    <Bone className="channels-skeleton__action" />
  </div>
);

/** The matrix (`ChannelsPage` in the agent's module): breadcrumb, then the overview card. */
const MatrixSkeleton: React.FC = () => (
  <>
    <div className="channels-skeleton__crumbs" aria-hidden="true">
      <Bone className="channels-skeleton__crumb" />
    </div>
    <div className="channels-skeleton__panel" aria-hidden="true">
      <div className="channels-skeleton__head">
        <div className="channels-skeleton__head-text">
          <Bone className="channels-skeleton__title" />
          <Bone className="channels-skeleton__line channels-skeleton__line--subtitle" />
        </div>
        <Bone className="channels-skeleton__button" />
      </div>
      <div className="channels-skeleton__banner">
        <Bone className="channels-skeleton__line channels-skeleton__line--banner" />
        <Bone className="channels-skeleton__pill" />
      </div>
      {CATEGORY_CARDS.map((cards, category) => (
        <div key={category} className="channels-skeleton__category">
          <div className="channels-skeleton__category-head">
            <Bone className="channels-skeleton__line channels-skeleton__line--category" />
          </div>
          <div className="channels-skeleton__grid">
            {Array.from({ length: cards }, (_, card) => (
              <CardSkeleton key={card} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </>
);

/** One channel's detail (`ChannelDetailLayout`): header, activation row, sections. */
const DetailSkeleton: React.FC = () => (
  <div className="channels-skeleton__panel channels-skeleton__panel--detail" aria-hidden="true">
    <div className="channels-skeleton__detail-head">
      <Bone className="channels-skeleton__avatar" />
      <div className="channels-skeleton__head-text">
        <Bone className="channels-skeleton__line channels-skeleton__line--detail-title" />
        <Bone className="channels-skeleton__line channels-skeleton__line--subtitle" />
      </div>
      <Bone className="channels-skeleton__button" />
    </div>
    <div className="channels-skeleton__activation">
      <Bone className="channels-skeleton__line channels-skeleton__line--banner" />
      <Bone className="channels-skeleton__switch" />
    </div>
    {DETAIL_SECTIONS.map((section) => (
      <div key={section} className="channels-skeleton__section">
        <Bone className="channels-skeleton__line channels-skeleton__line--section-title" />
        <Bone className="channels-skeleton__line channels-skeleton__line--desc" />
        <Bone className="channels-skeleton__field" />
      </div>
    ))}
  </div>
);

export interface ChannelsSkeletonProps {
  /**
   * Which screen is loading. Omitted, it is read from the current path — which
   * needs a Router above (the route's Suspense fallback has one).
   */
  kind?: ChannelsSkeletonKind;
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
}

/**
 * Loading shape of the agent's Channels module (SL-1945). One shape from a cold
 * load to data: `RouteSkeleton` (inside the app chrome), the agent layout while
 * the agent loads and the route's Suspense fallback draw this; the detail page
 * keeps drawing it while the agent's saved channels arrive, instead of a form
 * full of defaults that rewrites itself a moment later.
 *
 * Its own classes and `--silia-*` tokens only: the page's sheets (and the
 * `--as-*` tokens its root defines) come with its chunk, and this paints before
 * it. Nothing visible but bones; the status carries its translated text inside,
 * visually hidden, so a screen reader announces it. No `aria-busy`: on a live
 * region it tells assistive tech to hold the announcement.
 */
const ChannelsSkeletonShape: React.FC<{ shape: ChannelsSkeletonKind; standalone: boolean }> = ({
  shape,
  standalone,
}) => {
  const { t } = useTranslation(NS);
  return (
    <div
      className={`channels-skeleton channels-skeleton--${shape}${
        standalone ? ' channels-skeleton--standalone' : ''
      }`}
      role="status"
      data-testid="channels-skeleton"
    >
      <span className="channels-skeleton__sr-only">{t(shape)}</span>
      {shape === 'detail' ? <DetailSkeleton /> : <MatrixSkeleton />}
    </div>
  );
};

/** Only the route's Suspense fallback needs this: it reads the shape from the path. */
const ChannelsSkeletonForPath: React.FC<{ standalone: boolean }> = ({ standalone }) => {
  const { pathname } = useLocation();
  return (
    <ChannelsSkeletonShape
      shape={channelsSkeletonKind(pathname) ?? 'matrix'}
      standalone={standalone}
    />
  );
};

export const ChannelsSkeleton: React.FC<ChannelsSkeletonProps> = ({ kind, standalone = false }) =>
  kind ? (
    <ChannelsSkeletonShape shape={kind} standalone={standalone} />
  ) : (
    <ChannelsSkeletonForPath standalone={standalone} />
  );
